import {
  ICoverCandidateResponse,
  IReassignSourceImpactResponse,
  ICoverageCellDetailResponse,
  ICoverageMatrixCellResponse,
  ICoverageMatrixResponse,
  ICoverageMatrixRowResponse,
  ICoverageModelResponse,
  ICoverageModelRowResponse,
  ICoverageNamedResponse,
  ICoveragePersonResponse,
  ICoverageShiftResponse,
  ICoverageUserResponse,
  ISaveCoverageModelBody,
} from 'types/api/coverageApi.interface'
import { IShiftCoverageSummaryResponse } from 'types/api/shiftCoverageApi.interface'

export const USE_COVERAGE_MOCKS = true

interface INamedDef {
  id: number
  name: string
}

const DEFAULT_SITES: INamedDef[] = [
  { id: 1, name: 'Site North' },
  { id: 2, name: 'Site South' },
]

const DEFAULT_DEPARTMENTS: INamedDef[] = [
  { id: 1, name: 'Mining' },
  { id: 2, name: 'Plant' },
  { id: 3, name: 'Maintenance' },
  { id: 4, name: 'Camp Services' },
  { id: 5, name: 'Security' },
]

let siteDefs: INamedDef[] = [...DEFAULT_SITES]
let deptDefs: INamedDef[] = [...DEFAULT_DEPARTMENTS]

export const coverageSites = (): INamedDef[] => siteDefs
export const coverageDepartments = (): INamedDef[] => deptDefs

interface ICoverageShiftDef {
  shiftId: number
  name: string
  windowFrom: string
  windowTo: string
}

const DEFAULT_SHIFTS: ICoverageShiftDef[] = [
  { shiftId: 1, name: 'Morning', windowFrom: '06:00', windowTo: '14:00' },
  { shiftId: 2, name: 'Afternoon', windowFrom: '14:00', windowTo: '22:00' },
  { shiftId: 3, name: 'Night', windowFrom: '22:00', windowTo: '06:00' },
  { shiftId: 4, name: 'Full Day', windowFrom: '06:00', windowTo: '18:00' },
  { shiftId: 9, name: 'test', windowFrom: '06:00', windowTo: '14:00' },
]

let shiftDefs: ICoverageShiftDef[] = [...DEFAULT_SHIFTS]
let registrySynced = false

export const coverageShifts = (): ICoverageShiftDef[] => shiftDefs

export const coverageRegistrySynced = (): boolean => registrySynced

const minToHHMM = (totalMin: number) => {
  const m = ((totalMin % 1440) + 1440) % 1440
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`
}

export const syncCoverageShiftsFromRegistry = (registry: any[]): void => {
  if (registrySynced) return
  const working = (registry || []).filter(
    (s: any) => s && !s.absence && s.shiftStart != null && s.minutes != null && s.minutes > 0,
  )
  if (!working.length) return
  const defs: ICoverageShiftDef[] = [...working]
    .sort((a: any, b: any) => a.shiftStart - b.shiftStart)
    .map((s: any) => ({
      shiftId: s.id,
      name: s.name || `Shift ${s.id}`,
      windowFrom: minToHHMM(s.shiftStart),
      windowTo: minToHHMM(s.shiftStart + s.minutes),
    }))
  const slotMap: { [slot: number]: number } = {}
  ;[1, 2, 3].forEach((slot, i) => {
    if (defs[i]) slotMap[slot] = defs[i].shiftId
  })
  models.forEach((m) => {
    if (slotMap[m.shiftId] != null) m.shiftId = slotMap[m.shiftId]
  })
  const oldPools = { ...POOLS }
  Object.keys(POOLS).forEach((k) => delete POOLS[k])
  Object.entries(oldPools).forEach(([key, value]) => {
    const [site, dept, shift] = key.split('-').map(Number)
    const mapped = slotMap[shift] ?? shift
    POOLS[`${site}-${dept}-${mapped}`] = value
  })
  shiftDefs = defs
  registrySynced = true
}

let sitesSynced = false
let deptsSynced = false
let rolesSynced = false
let capsSynced = false

const remapPoolDim = (dim: number, remap: { [old: number]: number }) => {
  const old = { ...POOLS }
  Object.keys(POOLS).forEach((k) => delete POOLS[k])
  Object.entries(old).forEach(([key, value]) => {
    const parts = key.split('-').map(Number)
    parts[dim] = remap[parts[dim]] ?? parts[dim]
    POOLS[`${parts[0]}-${parts[1]}-${parts[2]}`] = value
  })
}

const remapMembersDim = (dim: number, remap: { [old: number]: number }) => {
  const old = { ...COVERAGE_DEPT_MEMBERS }
  Object.keys(COVERAGE_DEPT_MEMBERS).forEach((k) => delete COVERAGE_DEPT_MEMBERS[k])
  Object.entries(old).forEach(([key, value]) => {
    const parts = key.split('-').map(Number)
    parts[dim] = remap[parts[dim]] ?? parts[dim]
    COVERAGE_DEPT_MEMBERS[`${parts[0]}-${parts[1]}`] = value
  })
}

const namedList = (list: any[]): INamedDef[] =>
  (list || [])
    .filter((x: any) => x && x.id != null && x.name)
    .map((x: any) => ({ id: Number(x.id), name: String(x.name) }))

const slotRemap = (defaults: INamedDef[], real: INamedDef[]): { [old: number]: number } => {
  const remap: { [old: number]: number } = {}
  const taken = new Set<number>()
  defaults.forEach((d) => {
    const match = real.find((r) => !taken.has(r.id) && r.name.toLowerCase() === d.name.toLowerCase())
    if (match) {
      remap[d.id] = match.id
      taken.add(match.id)
    }
  })
  defaults.forEach((d) => {
    if (remap[d.id] != null) return
    const match = real.find((r) => !taken.has(r.id))
    if (match) {
      remap[d.id] = match.id
      taken.add(match.id)
    }
  })
  return remap
}

export const syncCoverageSitesFromRegistry = (treeRoot: any): void => {
  if (sitesSynced || !treeRoot) return
  const roots: any[] =
    treeRoot.id === 0 && treeRoot.name === 'Sites' && Array.isArray(treeRoot.children)
      ? treeRoot.children
      : [treeRoot]
  const realSites = namedList(roots)
  if (!realSites.length) return
  const locs: ICoverageLocationDef[] = []
  const walk = (node: any, rootId: number) => {
    ;(node.children || []).forEach((c: any) => {
      if (c && c.id != null && c.name) {
        locs.push({ id: Number(c.id), siteNodeId: rootId, name: String(c.name) })
        walk(c, rootId)
      }
    })
  }
  roots.forEach((r: any) => walk(r, Number(r.id)))
  const siteRemap = slotRemap(DEFAULT_SITES, realSites)
  models.forEach((m) => {
    if (siteRemap[m.siteNodeId] != null) m.siteNodeId = siteRemap[m.siteNodeId]
  })
  remapPoolDim(0, siteRemap)
  remapMembersDim(0, siteRemap)
  const locRemap: { [old: number]: number } = {}
  DEFAULT_LOCATIONS.forEach((dl) => {
    const mappedSite = siteRemap[dl.siteNodeId] ?? dl.siteNodeId
    const match = locs.find((l) => l.siteNodeId === mappedSite && l.name.toLowerCase() === dl.name.toLowerCase())
    if (match) locRemap[dl.id] = match.id
    else {
      const ghost = -dl.id
      locs.push({ id: ghost, siteNodeId: mappedSite, name: dl.name })
      locRemap[dl.id] = ghost
    }
  })
  models.forEach((m) =>
    m.rows.forEach((r) => {
      if (r.locationNodeId != null && locRemap[r.locationNodeId] != null) r.locationNodeId = locRemap[r.locationNodeId]
    }),
  )
  siteDefs = realSites
  locDefs = locs
  sitesSynced = true
}

export const syncCoverageDepartmentsFromRegistry = (list: any[]): void => {
  if (deptsSynced) return
  const real = namedList(list)
  if (!real.length) return
  const remap = slotRemap(DEFAULT_DEPARTMENTS, real)
  models.forEach((m) => {
    if (remap[m.departmentId] != null) m.departmentId = remap[m.departmentId]
  })
  remapPoolDim(1, remap)
  remapMembersDim(1, remap)
  deptDefs = real
  deptsSynced = true
}

const syncNamedWithGhosts = (
  defaults: INamedDef[],
  list: any[],
  applyRemap: (remap: { [old: number]: number }) => void,
): INamedDef[] | null => {
  const real = namedList(list)
  if (!real.length) return null
  const defs = [...real]
  const remap: { [old: number]: number } = {}
  defaults.forEach((d) => {
    const match = real.find((r) => r.name.toLowerCase() === d.name.toLowerCase())
    if (match) remap[d.id] = match.id
    else {
      const ghost = -d.id
      defs.push({ id: ghost, name: d.name })
      remap[d.id] = ghost
    }
  })
  applyRemap(remap)
  return defs
}

export const syncCoverageRolesFromRegistry = (list: any[]): void => {
  if (rolesSynced) return
  const defs = syncNamedWithGhosts(DEFAULT_ROLES, list, (remap) =>
    models.forEach((m) =>
      m.rows.forEach((r) => {
        if (r.requirementType === 'role' && r.roleId != null && remap[r.roleId] != null) r.roleId = remap[r.roleId]
      }),
    ),
  )
  if (!defs) return
  roleDefs = defs
  rolesSynced = true
}

export const syncCoverageCapabilitiesFromRegistry = (list: any[]): void => {
  if (capsSynced) return
  const defs = syncNamedWithGhosts(DEFAULT_CAPABILITIES, list, (remap) =>
    models.forEach((m) =>
      m.rows.forEach((r) => {
        if (r.requirementType === 'capability' && r.capabilityId != null && remap[r.capabilityId] != null) {
          r.capabilityId = remap[r.capabilityId]
        }
      }),
    ),
  )
  if (!defs) return
  capDefs = defs
  capsSynced = true
}

export const syncCoverageRegistries = (rootState: any): void => {
  syncCoverageShiftsFromRegistry(rootState?.shifts?.shifts || [])
  syncCoverageSitesFromRegistry(rootState?.locationRegistry?.tree || null)
  syncCoverageDepartmentsFromRegistry(rootState?.departments?.departments || [])
  syncCoverageRolesFromRegistry(rootState?.clientRoles?.roles || [])
  syncCoverageCapabilitiesFromRegistry(rootState?.capabilities?.data || [])
}

const DEFAULT_ROLES: INamedDef[] = [
  { id: 1, name: 'Operator' },
  { id: 2, name: 'Truck driver' },
  { id: 3, name: 'Plant operator' },
  { id: 4, name: 'Mechanic' },
  { id: 5, name: 'Camp manager' },
  { id: 6, name: 'Camp supervisor' },
  { id: 7, name: 'Cleaner' },
  { id: 8, name: 'Cook' },
  { id: 9, name: 'Gate guard' },
]

const DEFAULT_CAPABILITIES: INamedDef[] = [
  { id: 101, name: 'MECH-2' },
  { id: 102, name: 'HV License' },
  { id: 103, name: 'First Aid' },
]

interface ICoverageLocationDef {
  id: number
  siteNodeId: number
  name: string
}

const DEFAULT_LOCATIONS: ICoverageLocationDef[] = [
  { id: 11, siteNodeId: 1, name: 'Main Gate' },
  { id: 12, siteNodeId: 1, name: 'East Gate' },
  { id: 13, siteNodeId: 1, name: 'Control Room' },
  { id: 14, siteNodeId: 1, name: 'Camp Kitchen' },
  { id: 15, siteNodeId: 1, name: 'Workshop' },
  { id: 21, siteNodeId: 2, name: 'South Gate' },
  { id: 22, siteNodeId: 2, name: 'ROM Pad' },
]

let roleDefs: INamedDef[] = [...DEFAULT_ROLES]
let capDefs: INamedDef[] = [...DEFAULT_CAPABILITIES]
let locDefs: ICoverageLocationDef[] = [...DEFAULT_LOCATIONS]

export const coverageRoles = (): INamedDef[] => roleDefs
export const coverageCapabilities = (): INamedDef[] => capDefs
export const coverageLocations = (): ICoverageLocationDef[] => locDefs

export const COVERAGE_DEPT_MEMBERS: { [siteDept: string]: number } = {
  '1-1': 30, '1-2': 12, '1-3': 4, '1-4': 6, '1-5': 6,
  '2-1': 6, '2-2': 4, '2-3': 1, '2-4': 2, '2-5': 1,
}

interface IDemoModelRow {
  count: number
  requirementType: 'role' | 'capability'
  roleId: number | null
  capabilityId: number | null
  critical: boolean
  locationNodeId: number | null
}

interface IDemoModel {
  id: number
  departmentId: number
  siteNodeId: number
  shiftId: number
  rows: IDemoModelRow[]
  effectiveFrom: string
  createdAt: string
}

const role = (count: number, roleId: number, critical = false, locationNodeId: number | null = null): IDemoModelRow => ({
  count, requirementType: 'role', roleId, capabilityId: null, critical, locationNodeId,
})
const cap = (count: number, capabilityId: number, critical = false): IDemoModelRow => ({
  count, requirementType: 'capability', roleId: null, capabilityId, critical, locationNodeId: null,
})

let nextModelId = 100
const model = (departmentId: number, siteNodeId: number, shiftId: number, rows: IDemoModelRow[], effectiveFrom = '2026-06-01'): IDemoModel => ({
  id: nextModelId++, departmentId, siteNodeId, shiftId, rows, effectiveFrom, createdAt: effectiveFrom + 'T08:00:00.000Z',
})

const models: IDemoModel[] = [
  model(1, 1, 1, [role(10, 1), role(2, 2)]),
  model(2, 1, 1, [role(5, 3)]),
  model(3, 1, 1, [role(2, 4), cap(1, 101, true)]),
  model(4, 1, 1, [role(1, 6), role(2, 7)]),
  model(5, 1, 1, [role(1, 9, true, 11), role(1, 9, false, 12)]),
  model(1, 1, 2, [role(8, 1), role(2, 2)]),
  model(2, 1, 2, [role(4, 3)]),
  model(3, 1, 2, [cap(1, 101, true)]),
  model(4, 1, 2, [role(1, 7), role(1, 8)]),
  model(5, 1, 2, [role(1, 9, true, 11), role(1, 9, false, 12)]),
  model(1, 1, 3, [role(5, 1)]),
  model(2, 1, 3, [role(2, 3)]),
  model(5, 1, 3, [role(2, 9)]),
  model(1, 2, 1, [role(6, 1)]),
  model(5, 2, 1, [role(1, 9, true, 21)]),
  model(2, 2, 2, [role(3, 3)]),
]

interface IDemoPerson {
  userId: number
  name: string
  roleLabel: string
  rowIdx: number
  active: 'present' | 'late' | 'absent' | 'break'
  future: 'confirmed' | 'no_response' | 'unavailable'
}

const P = (userId: number, name: string, roleLabel: string, rowIdx: number, active: IDemoPerson['active'] = 'present', future: IDemoPerson['future'] = 'confirmed'): IDemoPerson => ({ userId, name, roleLabel, rowIdx, active, future })

const POOLS: { [key: string]: IDemoPerson[] } = {
  '1-1-1': [
    P(1, 'Peter M.', 'Operator', 0), P(2, 'Michael K.', 'Operator', 0), P(3, 'John S.', 'Operator', 0),
    P(4, 'Daniel R.', 'Operator', 0), P(5, 'Anna V.', 'Operator', 0), P(6, 'Luka J.', 'Operator', 0),
    P(7, 'Stefan D.', 'Operator', 0), P(8, 'Emma W.', 'Operator', 0, 'late'), P(9, 'Noah B.', 'Operator', 0),
    P(10, 'Mia T.', 'Operator', 0), P(11, 'Igor P.', 'Operator', 0),
    P(12, 'Omar H.', 'Truck driver', 1), P(13, 'Sara K.', 'Truck driver', 1),
  ],
  '1-2-1': [
    P(20, 'Grace M.', 'Plant operator', 0), P(21, 'Filip N.', 'Plant operator', 0), P(22, 'Jana R.', 'Plant operator', 0),
    P(23, 'Marko S.', 'Plant operator', 0), P(24, 'Lea D.', 'Plant operator', 0), P(25, 'Tom H.', 'Plant operator', 0, 'break'),
  ],
  '1-3-1': [
    P(30, 'Nicholas T.', 'Mechanic', 0), P(31, 'Sofia R.', 'Mechanic', 0), P(32, 'Ivan K.', 'MECH-2', 1),
  ],
  '1-4-1': [
    P(40, 'Thomas P.', 'Camp supervisor', 0), P(41, 'Robert L.', 'Cleaner', 1), P(42, 'Nina V.', 'Cleaner', 1),
  ],
  '1-5-1': [
    P(50, 'Aleks G.', 'Gate guard', 0), P(51, 'Marco B.', 'Gate guard', 1),
  ],
  '1-1-2': [
    P(60, 'Pavle M.', 'Operator', 0), P(61, 'Dario F.', 'Operator', 0), P(62, 'Tara Z.', 'Operator', 0),
    P(63, 'Vuk L.', 'Operator', 0), P(64, 'Ena S.', 'Operator', 0), P(65, 'Rade K.', 'Operator', 0),
    P(66, 'Iva B.', 'Operator', 0), P(67, 'Ognjen T.', 'Operator', 0),
    P(68, 'Milan D.', 'Truck driver', 1), P(69, 'Jovana P.', 'Truck driver', 1),
  ],
  '1-2-2': [
    P(70, 'Petra N.', 'Plant operator', 0), P(71, 'Andrej V.', 'Plant operator', 0),
    P(72, 'Katarina J.', 'Plant operator', 0), P(73, 'Boris M.', 'Plant operator', 0),
  ],
  '1-3-2': [P(74, 'Ivan K.', 'MECH-2', 0)],
  '1-4-2': [P(75, 'Robert L.', 'Cleaner', 0), P(76, 'Zora D.', 'Cook', 1)],
  '1-5-2': [
    P(77, 'Diego Soto', 'Gate guard', 0, 'absent', 'no_response'),
    P(78, 'Marco B.', 'Gate guard', 1),
  ],
  '1-1-3': [
    P(80, 'Nemanja S.', 'Operator', 0), P(81, 'Olga F.', 'Operator', 0), P(82, 'Damir H.', 'Operator', 0),
    P(83, 'Tijana R.', 'Operator', 0), P(84, 'Goran B.', 'Operator', 0),
  ],
  '1-2-3': [P(85, 'Vera N.', 'Plant operator', 0), P(86, 'Lazar K.', 'Plant operator', 0)],
  '1-5-3': [P(87, 'Ana M.', 'Gate guard', 0), P(88, 'Dusan V.', 'Gate guard', 0)],
  '2-1-1': [
    P(90, 'Nikola T.', 'Operator', 0), P(91, 'Maja S.', 'Operator', 0), P(92, 'Zoran P.', 'Operator', 0),
    P(93, 'Elena D.', 'Operator', 0), P(94, 'Vlado K.', 'Operator', 0),
  ],
  '2-5-1': [P(95, 'Branko J.', 'Gate guard', 0)],
  '2-2-2': [P(96, 'Sanja L.', 'Plant operator', 0), P(97, 'Dejan R.', 'Plant operator', 0), P(98, 'Mira B.', 'Plant operator', 0)],
}

const DEMO_BENCH: { [deptSlot: number]: string[] } = {
  1: ['Relja Novak', 'Ivona Peric', 'Matej Simic'],
  2: ['Dunja Ilic', 'Ognjen Savic'],
  3: ['Petar Zoric'],
  4: ['Lana Kovac', 'Emir Hodzic'],
  5: ['Vasko Ristic', 'Teodora Lukic'],
}

const todayStr = () => new Date().toISOString().slice(0, 10)

const toMin = (hhmm: string) => {
  const [h, m] = hhmm.split(':').map(Number)
  return h * 60 + m
}

const phaseStringFor = (windowFrom: string, windowTo: string, dateStr?: string): 'active' | 'future' | 'past' => {
  const date = dateStr || todayStr()
  const today = todayStr()
  if (date < today) return 'past'
  if (date > today) return 'future'
  const now = new Date()
  const nowMin = now.getHours() * 60 + now.getMinutes()
  const from = toMin(windowFrom)
  const to = toMin(windowTo)
  if (to < from) return nowMin >= from ? 'active' : 'future'
  if (nowMin >= to) return 'past'
  if (nowMin >= from) return 'active'
  return 'future'
}

const PHASE_NUM: { [k: string]: number } = { future: 1, active: 2, past: 3 }

const named = (id: number | null, list: INamedDef[] | ICoverageLocationDef[]): ICoverageNamedResponse | null => {
  if (id == null) return null
  const hit = (list as INamedDef[]).find((x) => x.id === id)
  return hit ? { id: hit.id, name: hit.name } : { id, name: `#${id}` }
}

const toShiftResponse = (def: ICoverageShiftDef): ICoverageShiftResponse => {
  const start = toMin(def.windowFrom)
  const end = toMin(def.windowTo)
  return { id: def.shiftId, name: def.name, shiftStart: start, minutes: ((end - start) % 1440 + 1440) % 1440 || 1440 }
}

const demoUser = (userId: number, name: string): ICoverageUserResponse => {
  const parts = name.split(' ')
  return { id: userId, firstName: parts[0], lastName: parts.slice(1).join(' ') }
}

const CREATOR: ICoverageUserResponse = { id: 1, firstName: 'Milos', lastName: 'M.' }

const latestVersionFor = (departmentId: number, siteNodeId: number, shiftId: number, dateStr?: string): IDemoModel | null => {
  const date = dateStr || todayStr()
  const candidates = models
    .filter((m) => m.departmentId === departmentId && m.siteNodeId === siteNodeId && m.shiftId === shiftId && m.effectiveFrom <= date)
    .sort((a, b) => (a.effectiveFrom < b.effectiveFrom ? 1 : -1))
  return candidates[0] || null
}

const activeModelFor = (departmentId: number, siteNodeId: number, shiftId: number, dateStr?: string): IDemoModel | null => {
  const winner = latestVersionFor(departmentId, siteNodeId, shiftId, dateStr)
  return winner && winner.rows.length ? winner : null
}

const toModelRowResponse = (r: IDemoModelRow, idx: number): ICoverageModelRowResponse => ({
  id: idx + 1,
  role: r.requirementType === 'role' ? named(r.roleId, roleDefs) : null,
  capability: r.requirementType === 'capability' ? named(r.capabilityId, capDefs) : null,
  requiredCount: r.count,
  isCritical: r.critical,
  requirementType: r.requirementType === 'role' ? 1 : 2,
  locationNode: r.locationNodeId != null ? named(r.locationNodeId, locDefs) : null,
})

const totalOf = (m: IDemoModel): number => m.rows.reduce((a, r) => a + r.count, 0)

const toModelResponse = (m: IDemoModel, totalRequiredChange = 0, staffingWarning: string | null = null): ICoverageModelResponse => {
  const def = shiftDefs.find((s) => s.shiftId === m.shiftId) || DEFAULT_SHIFTS[0]
  return {
    id: m.id,
    department: named(m.departmentId, deptDefs) as ICoverageNamedResponse,
    site: named(m.siteNodeId, siteDefs) as ICoverageNamedResponse,
    shift: toShiftResponse(def),
    effectiveFrom: m.effectiveFrom,
    createdBy: CREATOR,
    coverageModelRows: m.rows.map(toModelRowResponse),
    isStaffed: m.rows.length > 0,
    totalRequired: totalOf(m),
    hasCriticalRows: m.rows.some((r) => r.critical),
    totalRequiredChange,
    staffingWarning,
    createdAt: m.createdAt,
    updatedAt: m.createdAt,
  }
}

const checkedIn = (p: IDemoPerson) => p.active === 'present' || p.active === 'late' || p.active === 'break'

const cellStatus = (requiredTotal: number, coveredTotal: number, hasCriticalGap: boolean): number => {
  if (!requiredTotal) return 1
  const ratio = coveredTotal / requiredTotal
  if (hasCriticalGap || ratio < 0.75) return 3
  if (ratio < 1) return 2
  return 1
}

const computeCell = (departmentId: number, siteNodeId: number, shiftId: number, dateStr?: string): ICoverageMatrixCellResponse => {
  const def = shiftDefs.find((s) => s.shiftId === shiftId) || DEFAULT_SHIFTS[0]
  const date = dateStr || todayStr()
  const phaseStr = phaseStringFor(def.windowFrom, def.windowTo, dateStr)
  const phase = PHASE_NUM[phaseStr]
  const m = activeModelFor(departmentId, siteNodeId, shiftId, dateStr)
  const shift = toShiftResponse(def)
  if (!m) {
    return {
      shift, occurrenceDate: date, isStaffed: false, phase, status: 1,
      requiredTotal: 0, coveredTotal: 0, hasCriticalGap: false,
      isFrozen: phase === 3, frozenAt: null, coverageMatrixRows: [],
    }
  }
  const pool = POOLS[`${siteNodeId}-${departmentId}-${shiftId}`] || []
  const rows: ICoverageMatrixRowResponse[] = m.rows.map((r, idx) => {
    const assigned = pool.filter((p) => p.rowIdx === idx)
    const confirmedCount = assigned.filter((p) => p.future === 'confirmed').length
    const actualCount = phaseStr === 'future' ? confirmedCount : assigned.filter(checkedIn).length
    return {
      role: r.requirementType === 'role' ? named(r.roleId, roleDefs) : null,
      capability: r.requirementType === 'capability' ? named(r.capabilityId, capDefs) : null,
      requiredCount: r.count,
      actualCount,
      rosteredCount: assigned.length,
      confirmedCount,
      unavailableCount: assigned.filter((p) => p.future === 'unavailable').length,
      isCritical: r.critical,
      isSatisfied: actualCount >= r.count,
      requirementType: r.requirementType === 'role' ? 1 : 2,
      locationNode: r.locationNodeId != null ? named(r.locationNodeId, locDefs) : null,
    }
  })
  const requiredTotal = rows.reduce((a, r) => a + r.requiredCount, 0)
  const coveredTotal = rows.reduce((a, r) => a + Math.min(r.actualCount, r.requiredCount), 0)
  const hasCriticalGap = rows.some((r) => r.isCritical && !r.isSatisfied)
  return {
    shift,
    occurrenceDate: date,
    isStaffed: true,
    phase,
    status: cellStatus(requiredTotal, coveredTotal, hasCriticalGap),
    requiredTotal,
    coveredTotal,
    hasCriticalGap,
    isFrozen: phase === 3,
    frozenAt: phase === 3 ? `${date}T${def.windowTo}:00.000Z` : null,
    coverageMatrixRows: rows,
  }
}

export const buildDemoMatrix = (siteNodeId: number, dateStr?: string): ICoverageMatrixResponse => {
  const date = dateStr || todayStr()
  const departments = deptDefs
    .map((d) => {
      const cells = shiftDefs.map((s) => computeCell(d.id, siteNodeId, s.shiftId, dateStr))
      const anyModel = models.some((m) => m.departmentId === d.id && m.siteNodeId === siteNodeId)
      const current = cells.find((c) => c.isStaffed)
      return anyModel && current
        ? {
            department: named(d.id, deptDefs) as ICoverageNamedResponse,
            coverageModelId: 0,
            coverageModelEffectiveFrom: '2026-06-01',
            coverageMatrixCells: cells,
          }
        : null
    })
    .filter(Boolean) as ICoverageMatrixResponse['departments']
  return {
    date,
    atMinutes: null,
    site: named(siteNodeId, siteDefs) as ICoverageNamedResponse,
    shifts: shiftDefs.map(toShiftResponse),
    departments,
  }
}

const FUTURE_STATE: { [k: string]: number } = { confirmed: 2, 'no_response': 1, unavailable: 7 }
const ACTIVE_STATE: { [k: string]: number } = { present: 3, late: 4, break: 5, absent: 6 }

export const buildDemoCell = (siteNodeId: number, departmentId: number, shiftId: number, dateStr?: string): ICoverageCellDetailResponse | null => {
  const m = activeModelFor(departmentId, siteNodeId, shiftId, dateStr)
  if (!m) return null
  const def = shiftDefs.find((s) => s.shiftId === shiftId) || DEFAULT_SHIFTS[0]
  const cell = computeCell(departmentId, siteNodeId, shiftId, dateStr)
  const date = dateStr || todayStr()
  const pool = POOLS[`${siteNodeId}-${departmentId}-${shiftId}`] || []
  const phaseStr = phaseStringFor(def.windowFrom, def.windowTo, dateStr)
  const people: ICoveragePersonResponse[] = []
  m.rows.forEach((r, idx) => {
    const roleObj = r.requirementType === 'role' ? named(r.roleId, roleDefs) : null
    const capObj = r.requirementType === 'capability' ? named(r.capabilityId, capDefs) : null
    const locObj = r.locationNodeId != null ? named(r.locationNodeId, locDefs) : null
    const assigned = pool.filter((p) => p.rowIdx === idx)
    assigned.forEach((p) => {
      const state = phaseStr === 'future' ? FUTURE_STATE[p.future] : ACTIVE_STATE[p.active]
      people.push({
        workScheduleId: 1000 + p.userId,
        user: demoUser(p.userId, p.name),
        role: roleObj,
        capability: capObj,
        locationNode: locObj,
        state,
        arrivedAt: state === 3 || state === 4 || state === 5 ? `${date}T${def.windowFrom}:00.000Z` : null,
        lateByMinutes: state === 4 ? 12 : state === 3 || state === 5 ? 0 : null,
        isConfirmed: p.future === 'confirmed',
        confirmedSource: p.future === 'confirmed' ? 1 : null,
        declineReason: p.future === 'unavailable' ? 'Not available' : null,
      })
    })
    const vacancies = Math.max(r.count - assigned.length, 0)
    for (let i = 0; i < vacancies; i += 1) {
      people.push({
        workScheduleId: null, user: null, role: roleObj, capability: capObj, locationNode: locObj,
        state: 8, arrivedAt: null, lateByMinutes: null, isConfirmed: false, confirmedSource: null, declineReason: null,
      })
    }
  })
  return {
    date,
    site: named(siteNodeId, siteDefs) as ICoverageNamedResponse,
    department: named(departmentId, deptDefs) as ICoverageNamedResponse,
    shift: toShiftResponse(def),
    cell,
    people,
  }
}

export const buildDemoWidgetSummary = (siteNodeId = 1): IShiftCoverageSummaryResponse => {
  const legacyFullDayId = registrySynced ? null : 4
  const shifts = shiftDefs
    .filter((s) => s.shiftId !== legacyFullDayId)
    .map((s) => {
      const deptCells = deptDefs
        .map((d) => ({ dept: d, cell: computeCell(d.id, siteNodeId, s.shiftId) }))
        .filter((x) => x.cell.isStaffed)
      const required = deptCells.reduce((a, x) => a + x.cell.requiredTotal, 0)
      const covered = deptCells.reduce((a, x) => a + x.cell.coveredTotal, 0)
      const criticalGap = deptCells.some((x) => x.cell.hasCriticalGap)
      const phaseNum = deptCells[0]?.cell.phase || PHASE_NUM[phaseStringFor(s.windowFrom, s.windowTo)]
      const phase: 'future' | 'active' | 'past' = phaseNum === 1 ? 'future' : phaseNum === 2 ? 'active' : 'past'
      return {
        shiftId: s.shiftId,
        name: s.name,
        windowFrom: s.windowFrom,
        windowTo: s.windowTo,
        phase,
        required,
        covered,
        confirmed: phase === 'future' ? covered : null,
        criticalGap,
        departments: deptCells.map((x) => {
          const gapRow = x.cell.coverageMatrixRows.find((r) => r.isCritical && !r.isSatisfied)
          const gapName = gapRow ? gapRow.role?.name || gapRow.capability?.name || 'position' : null
          return {
            departmentId: x.dept.id,
            name: x.dept.name,
            required: x.cell.requiredTotal,
            covered: x.cell.coveredTotal,
            criticalGap: x.cell.hasCriticalGap,
            gapLabel: gapName ? `${gapName}${phase === 'future' ? ' UNCONFIRMED' : ' unfilled'}` : null,
          }
        }),
      }
    })
    .filter((s) => s.required > 0)

  const fullDay = registrySynced ? null : shiftDefs.find((s) => s.shiftId === 4)
  if (fullDay) {
    shifts.push({
      shiftId: 4, name: fullDay.name, windowFrom: fullDay.windowFrom, windowTo: fullDay.windowTo,
      phase: phaseStringFor(fullDay.windowFrom, fullDay.windowTo), required: 0, covered: siteNodeId === 1 ? 3 : 0,
      confirmed: null, criticalGap: false, departments: [],
    })
  }

  const noReqs = shiftDefs.filter(
    (s) => s.shiftId !== legacyFullDayId && !deptDefs.some((d) => activeModelFor(d.id, siteNodeId, s.shiftId)),
  )
  return {
    siteName: siteDefs.find((s) => s.id === siteNodeId)?.name || 'Site',
    shifts,
    shiftsWithoutRequirements: noReqs.map((s) => ({ shiftId: s.shiftId, name: s.name })),
  }
}

export const demoModelsInForce = (departmentId: number, siteNodeId: number, onDate?: string): ICoverageModelResponse[] =>
  shiftDefs
    .map((s) => latestVersionFor(departmentId, siteNodeId, s.shiftId, onDate))
    .filter(Boolean)
    .map((m) => toModelResponse(m as IDemoModel))

export const demoModelVersions = (departmentId: number, siteNodeId: number): ICoverageModelResponse[] => {
  const mine = models
    .filter((m) => m.departmentId === departmentId && m.siteNodeId === siteNodeId)
    .sort((a, b) => (a.effectiveFrom < b.effectiveFrom ? 1 : a.effectiveFrom > b.effectiveFrom ? -1 : b.id - a.id))
  return mine.map((m) => {
    const prev = models
      .filter((x) => x.departmentId === m.departmentId && x.siteNodeId === m.siteNodeId && x.shiftId === m.shiftId
        && (x.effectiveFrom < m.effectiveFrom || (x.effectiveFrom === m.effectiveFrom && x.id < m.id)))
      .sort((a, b) => (a.effectiveFrom < b.effectiveFrom ? 1 : -1))[0]
    return toModelResponse(m, prev ? totalOf(m) - totalOf(prev) : 0)
  })
}

export const saveDemoModels = (body: ISaveCoverageModelBody): ICoverageModelResponse[] => {
  const saved: ICoverageModelResponse[] = []
  body.coverageModelShifts.forEach((entry) => {
    const rows: IDemoModelRow[] = entry.coverageModelRows.map((r) => ({
      count: r.requiredCount,
      requirementType: r.roleId != null ? 'role' : 'capability',
      roleId: r.roleId,
      capabilityId: r.capabilityId,
      critical: !!r.isCritical,
      locationNodeId: r.locationNodeId ?? null,
    }))
    const current = latestVersionFor(body.departmentId, body.siteNodeId, entry.shiftId, body.effectiveFrom)
    if (JSON.stringify(current?.rows || []) === JSON.stringify(rows)) return
    const created = model(body.departmentId, body.siteNodeId, entry.shiftId, rows, body.effectiveFrom)
    models.push(created)
    const total = totalOf(created)
    const membersAvailable = COVERAGE_DEPT_MEMBERS[`${body.siteNodeId}-${body.departmentId}`]
    const warning =
      membersAvailable != null && total > membersAvailable
        ? `Requires ${total}, department has ${membersAvailable} members on this site.`
        : null
    saved.push(toModelResponse(created, current ? total - totalOf(current) : 0, warning))
  })
  return saved
}

export const demoCoverCandidates = (siteNodeId: number, departmentId: number): ICoverCandidateResponse[] => {
  const slot = ((departmentId % 5) || 5) as number
  return (DEMO_BENCH[slot] || []).map((name, i) => ({
    user: demoUser(9000 + departmentId * 10 + i, name),
    isAdditive: true,
    currentShift: null,
  }))
}

export const demoApplyCover = (userId: number, shiftId: number, siteNodeId: number, departmentId: number): void => {
  const key = `${siteNodeId}-${departmentId}-${shiftId}`
  if (!POOLS[key]) POOLS[key] = []
  const bench = Object.values(DEMO_BENCH).flat()
  const name = bench[userId % bench.length] || 'Cover Person'
  POOLS[key].push(P(userId, name, 'Cover', 0, 'present', 'confirmed'))
}

export const demoConfirmSlot = (workScheduleId: number): void => {
  const userId = workScheduleId - 1000
  Object.values(POOLS).forEach((pool) =>
    pool.forEach((p) => {
      if (p.userId === userId) p.future = 'confirmed'
    }),
  )
}

export const demoRevokeConfirm = (workScheduleId: number): void => {
  const userId = workScheduleId - 1000
  Object.values(POOLS).forEach((pool) =>
    pool.forEach((p) => {
      if (p.userId === userId) p.future = 'no_response'
    }),
  )
}

export const demoReminderCount = (siteNodeId: number, departmentId: number, shiftId: number): number =>
  (POOLS[`${siteNodeId}-${departmentId}-${shiftId}`] || []).filter((p) => p.future === 'no_response').length

const findPersonPool = (workScheduleId: number): { key: string; idx: number } | null => {
  const userId = workScheduleId - 1000
  const keys = Object.keys(POOLS)
  for (let i = 0; i < keys.length; i += 1) {
    const idx = POOLS[keys[i]].findIndex((p) => p.userId === userId)
    if (idx >= 0) return { key: keys[i], idx }
  }
  return null
}

export const demoReassignPreview = (workScheduleId: number, targetShiftId: number): IReassignSourceImpactResponse => {
  const hit = findPersonPool(workScheduleId)
  const targetExists = shiftDefs.some((s) => s.shiftId === targetShiftId)
  const fallbackShift = toShiftResponse(shiftDefs[0])
  if (!hit || !targetExists) {
    return { sourceShift: fallbackShift, statusBefore: 1, statusAfter: 1, degradesSource: false, createsCriticalGapAtSource: false }
  }
  const [site, dept, shift] = hit.key.split('-').map(Number)
  const def = shiftDefs.find((s) => s.shiftId === shift) || shiftDefs[0]
  const before = computeCell(dept, site, shift)
  const pool = POOLS[hit.key]
  const [person] = pool.splice(hit.idx, 1)
  const after = computeCell(dept, site, shift)
  pool.splice(hit.idx, 0, person)
  return {
    sourceShift: toShiftResponse(def),
    statusBefore: before.status,
    statusAfter: after.status,
    degradesSource: after.status > before.status,
    createsCriticalGapAtSource: after.hasCriticalGap && !before.hasCriticalGap,
  }
}

export const demoApplyReassign = (workScheduleId: number, targetShiftId: number): void => {
  const hit = findPersonPool(workScheduleId)
  if (!hit) return
  const [site, dept] = hit.key.split('-').map(Number)
  const [person] = POOLS[hit.key].splice(hit.idx, 1)
  const targetKey = `${site}-${dept}-${targetShiftId}`
  if (!POOLS[targetKey]) POOLS[targetKey] = []
  POOLS[targetKey].push({ ...person, rowIdx: 0, future: 'no_response' })
}
